import React, { useState, useCallback } from 'react';
import { FileText, Plus, Check, Sparkle, CaretUp, CaretDown, Trash, DotsSixVertical } from '@phosphor-icons/react';

export interface StoryCard {
  id: string;
  slideNum: number;
  title: string;
  body: string;
  tag: string;
}

interface StoryboardPanelProps {
  cards: StoryCard[];
  audience?: string;
  narrative?: string;
  isGenerating: boolean;
  currentActivity: string;
  onCardsChange: (cards: StoryCard[]) => void;
  onApproveGenerate: () => void;
  hasGeneratedBefore?: boolean;
  allowStructureEdits?: boolean;
}

const DEFAULT_CARDS: StoryCard[] = [];

export default function StoryboardPanel({
  cards, audience, narrative, isGenerating, currentActivity,
  onCardsChange, onApproveGenerate, hasGeneratedBefore, allowStructureEdits = true,
}: StoryboardPanelProps) {
  const isBusy = isGenerating;
  const [editingId, setEditingId] = useState<string | null>(null);
  const [dragIdx, setDragIdx] = useState<number | null>(null);

  const updateCard = useCallback((id: string, field: 'title' | 'body' | 'tag', value: string) => {
    onCardsChange(cards.map(c => c.id === id ? { ...c, [field]: value } : c));
  }, [cards, onCardsChange]);

  const moveCard = useCallback((idx: number, dir: -1 | 1) => {
    const target = idx + dir;
    if (target < 0 || target >= cards.length) return;
    const next = [...cards];
    [next[idx], next[target]] = [next[target], next[idx]];
    // Renumber
    next.forEach((c, i) => c.slideNum = i + 1);
    onCardsChange(next);
  }, [cards, onCardsChange]);

  const deleteCard = useCallback((idx: number) => {
    const next = cards.filter((_, i) => i !== idx);
    next.forEach((c, i) => c.slideNum = i + 1);
    onCardsChange(next);
  }, [cards, onCardsChange]);

  const addCard = useCallback(() => {
    const newCard: StoryCard = {
      id: `slide-${Date.now()}`,
      slideNum: cards.length + 1,
      title: 'New Slide',
      body: 'Describe this slide content...',
      tag: '',
    };
    onCardsChange([...cards, newCard]);
    setEditingId(newCard.id);
  }, [cards, onCardsChange]);

  // Drag and drop handlers
  const handleDragStart = useCallback((idx: number) => {
    setDragIdx(idx);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, idx: number) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    const next = [...cards];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(idx, 0, moved);
    next.forEach((c, i) => c.slideNum = i + 1);
    onCardsChange(next);
    setDragIdx(idx);
  }, [dragIdx, cards, onCardsChange]);

  const handleDragEnd = useCallback(() => {
    setDragIdx(null);
  }, []);

  // Empty state
  if (cards.length === 0) {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'var(--bg-primary)' }}>
        {/* Toolbar */}
        <div style={{
          minHeight: 44, display: 'flex', flexWrap:'wrap', gap:8, alignItems: 'center', justifyContent: 'space-between',
          padding: '0 16px', background: 'var(--bg-surface)',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', flexWrap:'wrap', alignItems: 'center', gap: 10 }}>
            <FileText size={16} weight="light" style={{ color: 'var(--ds-accent)' }} />
            <span style={{ fontSize: 'var(--fs-lg)', fontWeight: 600, color: 'var(--text-primary)' }}>Narrative Plan</span>
          </div>
        </div>

        {/* Empty state */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
          <div style={{ textAlign: 'center', maxWidth: 400 }}>
            <Sparkle size={24} weight="light" style={{ margin: '0 auto 12px', color: 'var(--ds-accent)' }} />
            <h2 style={{ fontSize: 'var(--fs-xl)', fontWeight: 600, marginBottom: 4, color: 'var(--text-primary)' }}>Storyboard</h2>
            <p style={{ fontSize: 'var(--fs-lg)', color: 'var(--text-secondary)' }}>
              {isBusy
                ? 'Agent is working on the storyboard...'
                : 'Ask the AI planner to generate your storyboard. The slide cards will appear here for editing and reordering.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="storyboard-panel" style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'var(--bg-primary)', minHeight: 0, position: 'relative' }}>
      <style>{`
        .storyboard-panel .storyboard-approve-bar { opacity: 1; transition: opacity 0.2s; }
        .storyboard-panel:hover .storyboard-approve-bar { opacity: 1 !important; }
      `}</style>
      {/* Toolbar */}
      <div style={{
        minHeight: 44, display: 'flex', flexWrap:'wrap', gap:8, alignItems: 'center', justifyContent: 'space-between',
        padding: '0 16px', background: 'var(--bg-surface)', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', flexWrap:'wrap', alignItems: 'center', gap: 10 }}>
          <FileText size={16} weight="light" style={{ color: 'var(--ds-accent)' }} />
          <span style={{ fontSize: 'var(--fs-lg)', fontWeight: 600, color: 'var(--text-primary)' }}>Narrative Plan</span>
          <span style={{ fontSize: 'var(--fs-base)', color: 'var(--text-muted)' }}>{cards.length} slides</span>
          <span style={{
            fontSize: 'var(--fs-sm)', padding: '3px 8px', borderRadius: 12,
            background: 'var(--ds-warning-soft)', color: 'var(--ds-warning)', fontWeight: 500,
          }}>{allowStructureEdits ? 'Draft — click to edit' : 'Edit slide content'}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {allowStructureEdits && <button
            onClick={addCard}
            disabled={isBusy}
            style={{
              padding: '4px 10px', fontSize: 'var(--fs-md)', fontWeight: 500, background: 'none',
              color: isBusy ? 'var(--text-muted)' : 'var(--text-secondary)', border: 'none',
              cursor: isBusy ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', gap: 6, borderRadius: 8,
              opacity: isBusy ? 0.5 : 1,
            }}
            onMouseOver={e => !isBusy && (e.currentTarget.style.background = 'var(--code-bg)')}
            onMouseOut={e => (e.currentTarget.style.background = 'none')}
          >
            <Plus size={12} weight="light" />
            Add slide
          </button>}
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ padding: '0 16px 8px', background: 'var(--bg-surface)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          {audience && <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>Audience: {audience}</span>}
          {narrative && <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>Narrative: {narrative}</span>}
        </div>
        <div style={{ height: 3, background: 'var(--border)', borderRadius: 2, overflow: 'hidden' }}>
          <div style={{
            height: '100%', background: 'linear-gradient(90deg, var(--ds-accent), var(--ds-accent-hover))',
            borderRadius: 2, width: '100%',
          }} />
        </div>
      </div>

      {/* Cards */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
        {cards.map((card, idx) => {
          const isEditing = editingId === card.id;
          return (
            <div
              key={card.id}
              draggable={allowStructureEdits}
              onDragStart={() => handleDragStart(idx)}
              onDragOver={e => handleDragOver(e, idx)}
              onDragEnd={handleDragEnd}
              onClick={() => setEditingId(card.id)}
              style={{
                background: 'var(--bg-surface)',
                border: `1px solid ${isEditing ? 'var(--ds-accent)' : 'var(--border)'}`,
                borderRadius: 12,
                padding: 16,
                marginBottom: 10,
                position: 'relative',
                cursor: 'default',
                transition: 'all 0.15s',
                boxShadow: isEditing ? '0 0 0 2px var(--ds-accent-soft)' : 'none',
                opacity: dragIdx === idx ? 0.5 : 1,
              }}
              onMouseOver={e => {
                if (!isEditing) {
                  e.currentTarget.style.borderColor = 'var(--ds-accent-soft)';
                  e.currentTarget.style.boxShadow = '0 2px 8px var(--ds-accent-soft)';
                }
                const handle = e.currentTarget.querySelector('[data-drag]') as HTMLElement;
                if (handle) handle.style.opacity = '1';
              }}
              onMouseOut={e => {
                if (!isEditing) {
                  e.currentTarget.style.borderColor = 'var(--border)';
                  e.currentTarget.style.boxShadow = 'none';
                }
                const handle = e.currentTarget.querySelector('[data-drag]') as HTMLElement;
                if (handle) handle.style.opacity = '0';
              }}
            >
              {/* Slide number badge */}
              <span style={{
                position: 'absolute', top: -8, left: 16, background: 'var(--ds-accent)', color: 'var(--ds-on-accent)',
                fontSize: 'var(--fs-sm)', fontWeight: 600, padding: '2px 8px', borderRadius: 4,
              }}>
                {card.slideNum}
              </span>

              {/* Drag handle */}
              {allowStructureEdits && <div
                data-drag
                style={{
                  position: 'absolute', top: '50%', left: -14, transform: 'translateY(-50%)',
                  cursor: 'grab', color: 'var(--border-strong)', opacity: 0, transition: 'opacity 0.15s',
                }}
              >
                <DotsSixVertical size={12} weight="light" />
              </div>}

              {/* Title */}
              <div
                contentEditable
                suppressContentEditableWarning
                onBlur={e => updateCard(card.id, 'title', e.currentTarget.textContent || '')}
                style={{
                  fontSize: 'var(--fs-lg)', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6,
                  outline: 'none',
                }}
              >
                {card.title}
              </div>

              {/* Body */}
              <div
                contentEditable
                suppressContentEditableWarning
                onBlur={e => updateCard(card.id, 'body', e.currentTarget.innerText)}
                style={{
                  fontSize: 'var(--fs-lg)', color: 'var(--text-secondary)', lineHeight: 1.6, outline: 'none',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {card.body}
              </div>

              {/* Actions */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 4,
                marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--code-bg)',
              }}>
                {isEditing ? (
                  <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--ds-accent)', fontWeight: 500 }}>Currently editing</span>
                ) : (
                  <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>{card.tag}</span>
                )}
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                  {/* Move up */}
                  {allowStructureEdits && idx > 0 && (
                    <button
                      title="Move up"
                      onClick={e => { e.stopPropagation(); moveCard(idx, -1); }}
                      style={{
                        padding: '3px 6px', background: 'none', border: 'none', cursor: 'pointer',
                        borderRadius: 6, display: 'flex', alignItems: 'center', color: 'var(--text-secondary)',
                      }}
                      onMouseOver={e => (e.currentTarget.style.background = 'var(--code-bg)')}
                      onMouseOut={e => (e.currentTarget.style.background = 'none')}
                    >
                      <CaretUp size={12} weight="light" />
                    </button>
                  )}
                  {/* Move down */}
                  {allowStructureEdits && idx < cards.length - 1 && (
                    <button
                      title="Move down"
                      onClick={e => { e.stopPropagation(); moveCard(idx, 1); }}
                      style={{
                        padding: '3px 6px', background: 'none', border: 'none', cursor: 'pointer',
                        borderRadius: 6, display: 'flex', alignItems: 'center', color: 'var(--text-secondary)',
                      }}
                      onMouseOver={e => (e.currentTarget.style.background = 'var(--code-bg)')}
                      onMouseOut={e => (e.currentTarget.style.background = 'none')}
                    >
                      <CaretDown size={12} weight="light" />
                    </button>
                  )}
                  {/* Delete */}
                  {allowStructureEdits && <button
                    title="Delete"
                    onClick={e => { e.stopPropagation(); deleteCard(idx); }}
                    style={{
                      padding: '3px 6px', background: 'none', border: 'none', cursor: 'pointer',
                      borderRadius: 6, display: 'flex', alignItems: 'center', color: 'var(--ds-danger)',
                    }}
                    onMouseOver={e => (e.currentTarget.style.background = 'var(--ds-danger-soft)')}
                    onMouseOut={e => (e.currentTarget.style.background = 'none')}
                  >
                    <Trash size={12} weight="light" />
                  </button>}
                </div>
              </div>
            </div>
          );
        })}

        {/* Add slide placeholder */}
        {!isBusy && allowStructureEdits && (
          <div
            onClick={addCard}
            style={{
              border: '2px dashed var(--border-strong)', borderRadius: 12, padding: 20,
              textAlign: 'center', cursor: 'pointer', transition: 'all 0.15s', marginBottom: 16,
            }}
            onMouseOver={e => { e.currentTarget.style.borderColor = 'var(--ds-accent)'; e.currentTarget.style.background = 'var(--ds-accent-soft)'; }}
            onMouseOut={e => { e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.background = 'transparent'; }}
          >
            <Plus size={16} weight="light" style={{ margin: '0 auto 4px', color: 'var(--text-muted)', display: 'block' }} />
            <span style={{ fontSize: 'var(--fs-md)', color: 'var(--text-muted)' }}>Add a slide</span>
          </div>
        )}
      </div>

      {/* Floating approve bar — bottom of panel, fades in on hover */}
      {!isBusy && cards.length > 0 && (
        <div className="storyboard-approve-bar" style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 10,
          background: 'linear-gradient(transparent, var(--bg-primary) 40%)',
          padding: '40px 0 16px', display: 'flex', justifyContent: 'center',
          pointerEvents: 'none',
        }}>
          <button
            onClick={onApproveGenerate}
            style={{
              padding: '6px 20px', fontSize: 'var(--fs-2xs)', fontWeight: 400,
              background: 'transparent', color: 'var(--text-muted)',
              border: '1px solid var(--border-strong)', borderRadius: 6,
              cursor: 'pointer', fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', gap: 6,
              pointerEvents: 'auto',
              transition: 'color 0.2s, border-color 0.2s',
            }}
            onMouseOver={e => { e.currentTarget.style.color = 'var(--ds-text)'; e.currentTarget.style.borderColor = 'var(--ds-text)'; }}
            onMouseOut={e => { e.currentTarget.style.color = 'var(--ds-text-muted)'; e.currentTarget.style.borderColor = 'var(--ds-border)'; }}
          >
            <Check size={10} weight="light" />
            {hasGeneratedBefore ? 'Regenerate' : 'Approve & Generate'}
          </button>
        </div>
      )}
    </div>
  );
}
